import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { ArrowRight, TrendingDown, Trophy, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  isOngoingChallengeResult,
  loadChallengeBannerResults,
} from '@/components/Layouts/challengeBannerResults'
import LiveAccountPartnersDialog from '@/components/Layouts/LiveAccountPartnersDialog'
import QrPromoStatusBanner from '@/components/Layouts/QrPromoStatusBanner'
import { publicConfig } from '@/config/publicConfig'
import {
  getTradingChallengeResultsBoard,
  getTradingChallengeResultsQueryOptions,
  type ListTradingChallengesQuery,
  type TradingChallenge,
  type TradingChallengeResult,
  tradingChallengeResultStatusEnum,
  tradingChallengeStatus,
  useGetUserSelf,
  useListTradingChallenges,
} from '@/hooks/v1'
import { isBondTraderAppCompany, isDoraAppCompany } from '@/utils/appBranding'

type ChallengeBannerKind = 'busted' | 'ended'

type ChallengeBannerState = {
  challenge: TradingChallenge
  kind: ChallengeBannerKind
}

// Development preview: temporarily set either flag to true to show that banner on any route.
const FORCE_BUSTED_BANNER_PREVIEW: boolean = false
const FORCE_ENDED_BANNER_PREVIEW: boolean = false

function getTradingChallengesListParams(): ListTradingChallengesQuery {
  const params: ListTradingChallengesQuery = {}

  if (!isDoraAppCompany()) {
    const tenantId = publicConfig.firebase.tenantId.trim()
    if (tenantId) params.tenant_id = tenantId
  }

  return params
}

function getDismissalKey({ challenge, kind }: ChallengeBannerState) {
  return `challenge-status-banner:${challenge.id}:${kind}`
}

export function selectChallengeBanner(
  challenges: TradingChallenge[],
  resultsByChallenge: Map<string, TradingChallengeResult | undefined>,
  currentUserId: string,
): ChallengeBannerState | null {
  const participations = challenges
    .filter((challenge) =>
      Boolean(resultsByChallenge.get(challenge.id) || challenge.users?.includes(currentUserId)),
    )
    .sort((left, right) => Date.parse(right.start_at) - Date.parse(left.start_at))

  const hasOngoingChallenge = participations.some(({ id, status }) => {
    return (
      status === tradingChallengeStatus.ACTIVE &&
      isOngoingChallengeResult(resultsByChallenge.get(id))
    )
  })

  if (hasOngoingChallenge) return null

  const mostRecentChallenge = participations[0]
  if (!mostRecentChallenge) return null

  if (
    resultsByChallenge.get(mostRecentChallenge.id)?.status ===
    tradingChallengeResultStatusEnum.BUSTED
  ) {
    return { challenge: mostRecentChallenge, kind: 'busted' }
  }

  if (mostRecentChallenge.status === tradingChallengeStatus.COMPLETED) {
    return { challenge: mostRecentChallenge, kind: 'ended' }
  }

  return null
}

const LegacyChallengeStatusBanner = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [dismissedKey, setDismissedKey] = useState<string | null>(null)
  const [isLiveAccountDialogOpen, setIsLiveAccountDialogOpen] = useState(false)
  const canOpenLiveAccount = isBondTraderAppCompany()
  const challengesListParams = useMemo(() => getTradingChallengesListParams(), [])
  const { data: userSelf } = useGetUserSelf()
  const currentUserId = userSelf?.data?.id
  const currentUserName = userSelf?.data?.user_name
  const { data: challengesResponse, isLoading: isLoadingChallenges } = useListTradingChallenges({
    query: challengesListParams,
  })

  const challenges = useMemo(
    () =>
      (challengesResponse?.data ?? []).filter(
        (challenge) =>
          challenge.type !== 'QR_PROMO' &&
          (challenge.status === tradingChallengeStatus.ACTIVE ||
            challenge.status === tradingChallengeStatus.COMPLETED),
      ),
    [challengesResponse?.data],
  )

  const resultsQuery = useQuery({
    queryKey: ['challenge-banner-results', currentUserId, currentUserName, challenges],
    enabled: Boolean(currentUserId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) =>
      Array.from(
        await loadChallengeBannerResults(
          challenges,
          currentUserId!,
          currentUserName,
          async (challenge) => {
            const response = await queryClient.fetchQuery({
              ...getTradingChallengeResultsQueryOptions({
                path: { trading_challenge_id: challenge.id },
                query: { board: getTradingChallengeResultsBoard.TOP_PNL },
              }),
              staleTime: 60_000,
            })
            return response.data ?? []
          },
          signal,
        ),
      ),
  })
  const resultsByChallenge = useMemo(() => new Map(resultsQuery.data ?? []), [resultsQuery.data])

  const banner = useMemo(
    () =>
      currentUserId ? selectChallengeBanner(challenges, resultsByChallenge, currentUserId) : null,
    [challenges, currentUserId, resultsByChallenge],
  )

  const isBustedPreview = import.meta.env.DEV && FORCE_BUSTED_BANNER_PREVIEW
  const isEndedPreview = import.meta.env.DEV && FORCE_ENDED_BANNER_PREVIEW
  const isPreview = isBustedPreview || isEndedPreview
  const isLoadingResults =
    Boolean(currentUserId) && (resultsQuery.isPending || resultsQuery.isError)
  if (!isPreview && (!banner || isLoadingChallenges || isLoadingResults)) return null

  const dismissalKey = isPreview
    ? `challenge-status-banner:preview:${isBustedPreview ? 'busted' : 'ended'}`
    : banner
      ? getDismissalKey(banner)
      : ''
  if (dismissedKey === dismissalKey) return null

  const handleDismiss = () => setDismissedKey(dismissalKey)

  const handleViewLeaderboard = () =>
    navigate({
      to: '/leaderboards',
      search: banner ? { challengeId: banner.challenge.id } : {},
    })

  const isBusted = isBustedPreview || (!isEndedPreview && banner?.kind === 'busted')
  const minimumEquityPercentage = banner?.challenge.minimum_equity_percentage_condition

  return (
    <>
      <section
        className={`relative w-full border-y text-[color:var(--color-text-2)] shadow-lg ${
          isBusted
            ? 'border-amber-500/20 bg-[linear-gradient(105deg,rgba(31,24,14,0.96),rgba(10,10,10,0.98))] px-4 py-3 sm:px-6'
            : 'border-primary/40 bg-[linear-gradient(115deg,rgba(0,35,44,0.98),rgba(3,17,24,0.98))] px-4 py-6 sm:px-6 sm:py-8'
        }`}
        role="alert"
      >
        <div
          className={`flex flex-col ${isBusted ? 'gap-3 pr-10 lg:flex-row lg:items-center lg:justify-between' : 'gap-6 pr-10 sm:pr-14 2xl:flex-row 2xl:items-center 2xl:justify-between'}`}
        >
          <div
            className={`flex min-w-0 items-start ${isBusted ? 'gap-3 sm:items-center sm:gap-4' : 'gap-4 sm:items-center sm:gap-6'}`}
          >
            {isBusted ? (
              <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-amber-400 sm:mt-0 sm:size-12">
                <TrendingDown className="size-6" strokeWidth={1.8} aria-hidden="true" />
              </span>
            ) : (
              <Trophy
                className="mt-1 size-14 shrink-0 text-primary sm:mt-0 sm:size-20"
                strokeWidth={1.6}
                aria-hidden="true"
              />
            )}
            <div className="min-w-0">
              <p className={isBusted ? 'text-sm' : 'text-xl leading-tight sm:text-2xl'}>
                <span className="font-bold">
                  {isBusted
                    ? 'This week’s Challenge has ended for your account'
                    : 'This week’s Challenge has ended'}
                </span>
              </p>
              {isBusted ? (
                <div className="mt-1 max-w-4xl text-sm leading-5 text-[color:var(--color-text-1)]">
                  <p>
                    You reached the{' '}
                    <span className="font-semibold text-amber-400">
                      {minimumEquityPercentage == null
                        ? 'maximum drawdown'
                        : `${minimumEquityPercentage}% maximum drawdown`}
                    </span>
                    , so trading is paused for the rest of this week’s Challenge.
                  </p>
                  <p className="font-medium text-[color:var(--color-text-2)]">
                    You’ll be able to participate again next week.
                  </p>
                </div>
              ) : (
                <div className="mt-2 max-w-4xl text-base leading-6 text-[color:var(--color-text-1)] sm:text-lg sm:leading-7">
                  <p>
                    Trading for this Challenge is now closed, and your final results have been
                    recorded.
                  </p>
                  <p className="mt-1 text-primary">
                    <span className="font-semibold">
                      Registration for next week’s challenge is open
                    </span>
                    {' — '}if you’ve already signed up, you’re in.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div
            className={`flex shrink-0 flex-col sm:flex-row sm:items-center ${isBusted ? 'gap-2' : 'gap-3'}`}
          >
            {canOpenLiveAccount && (
              <button
                type="button"
                onClick={() => setIsLiveAccountDialogOpen(true)}
                className={
                  isBusted
                    ? 'min-h-10 rounded-md border border-primary/60 bg-primary/10 px-6 text-sm font-medium text-primary transition-colors hover:bg-primary/20'
                    : 'inline-flex min-h-12 items-center justify-center gap-3 rounded-lg border border-primary bg-primary px-6 text-base font-semibold text-white transition-colors hover:bg-primary/90 sm:min-h-14'
                }
              >
                Open a Live Account
                {!isBusted && <ArrowRight className="size-5" aria-hidden="true" />}
              </button>
            )}
            <button
              type="button"
              onClick={handleViewLeaderboard}
              className={
                isBusted
                  ? 'inline-flex min-h-10 items-center justify-center gap-2 px-3 text-sm font-medium transition-colors hover:text-primary'
                  : 'inline-flex min-h-12 items-center justify-center gap-3 rounded-lg border border-primary/60 bg-primary/5 px-6 text-base font-semibold transition-colors hover:border-primary hover:bg-primary/10 sm:min-h-14'
              }
            >
              {isBusted ? 'View Leaderboard' : 'View Final Results'}
              <ArrowRight className="size-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          className={`absolute right-3 flex items-center justify-center rounded border border-[var(--color-border-strong)] text-[var(--color-text-2)] transition-colors duration-200 hover:bg-[var(--color-layer-4)] ${isBusted ? 'top-1/2 size-8 -translate-y-1/2' : 'top-6 size-9 sm:right-5 2xl:top-1/2 2xl:-translate-y-1/2'}`}
          aria-label="Dismiss challenge notice"
          title="Dismiss notice"
        >
          <X className="size-4" />
        </button>
      </section>

      {canOpenLiveAccount && (
        <LiveAccountPartnersDialog
          isOpen={isLiveAccountDialogOpen}
          onOpenChange={setIsLiveAccountDialogOpen}
        />
      )}
    </>
  )
}

export default function ChallengeStatusBanner() {
  return (
    <>
      <QrPromoStatusBanner />
      <LegacyChallengeStatusBanner />
    </>
  )
}
